import { Box, TableCell, TableRow } from "@mui/material";
import { DotLegalButton, DotLegalDialog, DotLegalHeader, DotLegalSelect, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import DotLegalTableWithControlsLocalized from "@common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import RiskScoringForm from "../../riskScoring/RiskScoringForm";
import LinkedControlsSection from "../../riskControls/LinkedControlsSection";
import { RiskOperationalItem } from "../RiskOperationalOverview.types";
import { useBulkAssessDialog } from "./BulkAssessDialog.hooks";
import { useBulkAssessDialogStyles } from "./BulkAssessDialog.styles";
import { BulkAssessEntityRow, BulkAssessScenarioRow } from "./BulkAssessDialog.types";
import Grid from "@mui/material/Grid2";

export interface IBulkAssessDialog {
    riskAreaId: string;
    entities: Array<RiskOperationalItem>;
    onRemoveEntity: (entityKey: string) => void;
    onSaved: () => void;
    onClose: () => void;
}

export function BulkAssessDialog(props: IBulkAssessDialog) {
    const styles = useBulkAssessDialogStyles();
    const { translateString } = useTranslation();

    const entityHeaders: Array<ITableHeader<BulkAssessEntityRow>> = [
        { property: "typeLabel", text: translateString("type") },
        { property: "name", text: translateString("entity") },
        { property: null, text: "", align: "right", notSortable: true },
    ];

    const scenarioHeaders: Array<ITableHeader<BulkAssessScenarioRow>> = [
        { property: "name", text: translateString("riskScenario") },
        { property: null, text: "", align: "right", notSortable: true },
    ];

    const {
        scoringForm,
        isLoading,
        isValid,
        entities,
        entityRows,
        removeEntity,
        availableScenarioOptions,
        addScenario,
        selectedScenarioRows,
        removeScenario,
        scenarioError,
        validateAndSave,
        inProgress,
        showLinkedControls,
        availableControls,
        frameworkControlIds,
        setFrameworkControlIds,
    } = useBulkAssessDialog(props);

    return (
        <DotLegalDialog
            open
            size="xl"
            isLoading={isLoading}
            inProgress={inProgress}
            header={translateString("bulkAssessment")}
            buttonOkText={translateString("assess")}
            onOkClick={validateAndSave}
            okButtonDisabled={!isValid}
            secondaryButtonText={translateString("cancel")}
            secondaryButtonStyle="secondary"
            onSecondaryButtonClick={props.onClose}
            onDialogClose={props.onClose}
        >
            <Box sx={styles.subHeader}>
                <DotLegalHeader center marginTop={-1} fontWeight={400} headerStyle="small">
                    {translateString("bulkAssessDescription")}
                </DotLegalHeader>
            </Box>

            <DotLegalSection>
                <Box sx={styles.entityTable}>
                    <DotLegalTableWithControlsLocalized
                        extraControls={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <DotLegalHeader headerStyle="small" color="primary" marginBottom={0}>
                                    {translateString("bulkAssessSelectedEntities", { count: entities.length })}
                                </DotLegalHeader>
                            </Box>
                        }
                        headers={entityHeaders}
                        data={entityRows}
                        isLoading={isLoading}
                        defaultOrderBy="name"
                        defaultOrder="asc"
                        columnsExcludedFromFilter={["key", "type"]}
                        getUserSpecificPageLength={() => 9999}
                        clickableRows={false}
                        emptyText={translateString("noData")}
                        renderRow={(row) => (
                            <TableRow key={row.key}>
                                <TableCell>{row.typeLabel}</TableCell>
                                <TableCell>
                                    <EntityTypeIconWithName type={row.type} name={row.name} />
                                </TableCell>
                                <TableCell align="right">
                                    <DotLegalButton buttonType="linkButton" size="small" onClick={() => removeEntity(row.key)}>
                                        {translateString("remove")}
                                    </DotLegalButton>
                                </TableCell>
                            </TableRow>
                        )}
                    />
                </Box>
            </DotLegalSection>

            <DotLegalSection>
                <DotLegalHeader headerStyle="small" color="primary" marginBottom={1}>
                    {translateString("chooseScenarios")}
                </DotLegalHeader>
                <Grid container columnSpacing={4}>
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ paddingRight: 2 }}>
                        {/* Single-select picker: each pick appends a row to the table below and the field clears, so
                        selected scenarios live only in the table (matches RelatedTasksSelector). */}
                        <DotLegalSelect
                            label=""
                            placeholder={translateString("addScenario")}
                            noMargin
                            options={availableScenarioOptions}
                            selectedItem=""
                            onChange={(value) => value && addScenario(value)}
                            clearOnSelect
                            noOptionsLabel={translateString("noOptions")}
                            errorText={scenarioError}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}></Grid>
                </Grid>
                {selectedScenarioRows.length > 0 && (
                    <Box sx={styles.scenarioTable}>
                        <DotLegalTable
                            headers={scenarioHeaders}
                            data={selectedScenarioRows}
                            isLoading={isLoading}
                            defaultOrderBy="name"
                            defaultOrder="asc"
                            hidePagination
                            clickableRows={false}
                            emptyText={translateString("noData")}
                            renderRow={(row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell align="right">
                                        <DotLegalButton buttonType="linkButton" size="small" onClick={() => removeScenario(row.id)}>
                                            {translateString("remove")}
                                        </DotLegalButton>
                                    </TableCell>
                                </TableRow>
                            )}
                        />
                    </Box>
                )}
            </DotLegalSection>

            <RiskScoringForm form={scoringForm} />

            {showLinkedControls && (
                <LinkedControlsSection linkedControlIds={frameworkControlIds} availableControls={availableControls} onChange={setFrameworkControlIds} />
            )}
        </DotLegalDialog>
    );
}

export default BulkAssessDialog;
